import React, { FC, useCallback, useRef } from 'react';

import {
	BUTTON_TYPES,
	EvIcon,
	EvPopup,
	EvSelectNew,
	EvTag,
	OptionType,
	TAG_STATUS,
	TITLE_MODES
} from '@evinced-private/ui-common';

import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';
import ArrowRightIcon from 'src/resources/icons/ArrowRight.svg?raw';

import { RemoveIconButton } from '../RemoveIconButton';
import { AddLabelTestsModalProps } from '../shared/types';

import { useLabelsManagement } from './hooks/useLabelsManagement';
import {
	CANCEL_BUTTON_TEXT,
	getModalTitle,
	MODAL_DESCRIPTION,
	SAVE_BUTTON_TEXT
} from './constants';
import { handleSave } from './handlers';

import './AddLabelTestsModal.scss';

export const AddLabelTestsModal: FC<AddLabelTestsModalProps> = ({
	isOpen,
	onClose,
	testRows,
	tenantConfiguredLabels,
	onSaveSuccess
}) => {
	const {
		selectedLabelType,
		setSelectedLabelType,
		selectedValue,
		setSelectedValue,
		additions,
		labelTypeOptions,
		valueOptions,
		removeAddition,
		resetAdditions
	} = useLabelsManagement({ testRows, tenantConfiguredLabels });

	const ariaLiveRef = useRef<HTMLDivElement>(null);
	const labelTypeSelectRef = useRef<HTMLInputElement>(null);

	const handleCloseAction = (): void => {
		resetAdditions();
		onClose();
	};

	const handleSaveAction = async (): Promise<void> => {
		await handleSave(testRows, additions, handleCloseAction, tenantConfiguredLabels, onSaveSuccess);
		resetAdditions();
	};

	const handleRemoveAddition = useCallback(
		(index: number, labelTypeLabel: string, valueLabel: string) => {
			removeAddition(index);
			if (ariaLiveRef.current) {
				ariaLiveRef.current.textContent = `${labelTypeLabel} with value ${valueLabel} removed from selection`;
			}
		},
		[removeAddition]
	);

	const handleValueChange = useCallback(
		(values: OptionType[]) => {
			setSelectedValue(values);
			if (values.length > 0 && selectedLabelType.length > 0 && ariaLiveRef.current) {
				ariaLiveRef.current.textContent = `${selectedLabelType[0].label} set to ${values[0].label}`;
			}
		},
		[selectedLabelType, setSelectedValue]
	);

	const handleLabelTypeChange = useCallback(
		(values: OptionType[]) => {
			setSelectedLabelType(values);
			setTimeout(() => {
				labelTypeSelectRef.current?.focus();
			}, 0);
		},
		[setSelectedLabelType]
	);

	const buttons = [
		{
			title: CANCEL_BUTTON_TEXT,
			onClick: handleCloseAction,
			type: BUTTON_TYPES.SECONDARY
		},
		{
			title: SAVE_BUTTON_TEXT,
			onClick: handleSaveAction
		}
	];

	return (
		<EvPopup
			appElement={PLATFORM_APP_ID}
			isOpen={isOpen}
			onClose={handleCloseAction}
			className="add-label-tests-modal"
			popupWidth="600px"
			title={getModalTitle(testRows)}
			titleMode={TITLE_MODES.MAIN}
			buttons={buttons}
			isCentered={true}
			isControlled
			isBgTransparent={true}
		>
			<div ref={ariaLiveRef} aria-live="polite" aria-atomic="true" className="sr-only" />
			<div className="add-label-tests-modal__content">
				<p id="add-label-tests-modal-description" className="add-label-tests-modal__description">
					{MODAL_DESCRIPTION}
				</p>

				<div className="add-label-tests-modal__selects-container">
					<div className="add-label-tests-modal__select-wrapper">
						<EvSelectNew
							id="label-type-select"
							label="Choose Label Type"
							options={labelTypeOptions}
							selectedValue={selectedLabelType}
							onSelectChange={handleLabelTypeChange}
							placeholder="Select a label type"
							listboxWidth="100%"
							showTooltip={true}
							tooltipContent="Select which type of label you want to add"
							shouldSortOptions={true}
						/>
					</div>

					<div className="add-label-tests-modal__arrow-separator">
						<EvIcon icon={ArrowRightIcon} />
					</div>

					<div className="add-label-tests-modal__select-wrapper">
						<EvSelectNew
							id="value-select"
							label="Value"
							options={valueOptions}
							selectedValue={selectedValue}
							onSelectChange={handleValueChange}
							placeholder="Select value"
							listboxWidth="100%"
							isDisabled={selectedLabelType.length === 0}
							showTooltip={true}
							tooltipContent="Select the value for the label"
							shouldSortOptions={true}
						/>
					</div>
				</div>

				{additions.length > 0 && (
					<>
						<div className="add-label-tests-modal__divider" />

						<div className="add-label-tests-modal__additions-section">
							<h3 className="add-label-tests-modal__additions-title">Labels to add</h3>
							<div className="add-label-tests-modal__additions-list">
								{additions.map((addition, index) => (
									<div
										key={`${addition.labelType.value}-${addition.value.value}`}
										className="add-label-tests-modal__addition-item"
									>
										<EvTag
											status={TAG_STATUS.INFO}
											text={`${addition.labelType.label}: ${addition.value.label}`}
											className="add-label-tests-modal__addition-tag"
										/>
										<RemoveIconButton
											onClick={() =>
												handleRemoveAddition(index, addition.labelType.label, addition.value.label)
											}
											ariaLabel={`Remove from selection Add: ${addition.labelType.label} with value ${addition.value.label}`}
											className="add-label-tests-modal__addition-remove-btn"
										/>
									</div>
								))}
							</div>
						</div>
					</>
				)}
			</div>
		</EvPopup>
	);
};
